import React, { useContext } from 'react';
import { StoresContext, StoreState } from '../../../App';

interface Feature {
    name?: string;
    type?: string;
    features: FeatureItem[];
}
interface FeatureRadioValue {
    name: string;
    value: string;
}
interface FeatureItem {
    id: string;
    name: string;
    value?: string | number | null;
    values?: FeatureRadioValue[];
}

interface Search {
    name?: string;
    type?: string;
    features: SearchItem[];
}

interface SearchItem {
    id: string;
    name: string;
    value: string;
}

interface FeaturesByFilter {
    dist?: Feature[];
    engineering?: Feature[];
    eshop?: Feature[];
    service?: Feature[];
    fsc?: Search[];
}

enum FeatureName {
    DIST = 'dist',
    ENGINEERING = 'engineering',
    ESHOP = 'eshop',
    SERVICE = 'service',
    FSC = 'fsc',
}

const makeRadioFeature = (id: string, name: string): FeatureItem => ({
    id,
    name,
    values: [
        { name: 'в наличии', value: 'stock' },
        { name: 'под заказ', value: 'order' },
    ],
});

const distributorFeatures = [
    {
        name: 'Товарные категории',
        features: [
            makeRadioFeature('DISTR0001', 'Преобразователи частоты'),
            makeRadioFeature('DISTR0002', 'Устройства плавного пуска'),
            makeRadioFeature('DISTR0003', 'Панели оператора'),
            makeRadioFeature('DISTR0004', 'Программируемые логические контроллеры'),
            makeRadioFeature('DISTR0005', 'Программируемые логические реле'),
            makeRadioFeature('DISTR0006', 'Блоки питания'),
            makeRadioFeature('DISTR0007', 'Двигатели'),
        ],
    },
];
const engineeringFeatures = [
    {
        name: 'Отрасль',
        features: [
            { id: 'ENG0001', name: 'Жилищно-коммунальное хозяйство' },
            { id: 'ENG0002', name: 'Нефтегазовая отрасль' },
            { id: 'ENG0003', name: 'Машиностроение' },
            { id: 'ENG0004', name: 'Пищевая промышленность' },
            { id: 'ENG0005', name: 'Энергетика' },
            { id: 'ENG0006', name: 'Металлургия и горнодобывающая промышленность' },
            { id: 'ENG0007', name: 'Химическая промышленность' },
            { id: 'ENG0008', name: 'Легкая промышленность' },
            { id: 'ENG0009', name: 'Сельское хозяйство' },
            { id: 'ENG0010', name: 'Строительство' },
            { id: 'ENG0011', name: 'Транспорт' },
        ],
    },
    {
        name: 'Специализация',
        features: [
            { id: 'ENG1001', name: 'Отопление, вентиляция и кондиционирование' },
            { id: 'ENG1002', name: 'Водоподготовка, водоснабжение и водоотведение' },
            { id: 'ENG1003', name: 'Электрораспределение и освещение' },
            { id: 'ENG1004', name: 'Автоматизация технологических процессов' },
            { id: 'ENG1005', name: 'Теплогенерация, парогенерация и распределение' },
            { id: 'ENG1006', name: 'Подъемно-транспортное оборудование' },
            { id: 'ENG1007', name: 'Насосное оборудование' },
            { id: 'ENG1008', name: 'Системы управления зданием' },
            { id: 'ENG1009', name: 'Конвейеры и упаковочное оборудование' },
            { id: 'ENG1010', name: 'Печи и системы нагрева' },
        ],
    },
];
const eshopFeatures = [
    {
        name: 'Товарные категории',
        features: [
            makeRadioFeature('ESHOP0001', 'Преобразователи частоты'),
            makeRadioFeature('ESHOP0002', 'Устройства плавного пуска'),
            makeRadioFeature('ESHOP0003', 'Панели оператора'),
            makeRadioFeature('ESHOP0004', 'Программируемые логические контроллеры'),
            makeRadioFeature('ESHOP0005', 'Программируемые логические реле'),
            makeRadioFeature('ESHOP0006', 'Блоки питания'),
            makeRadioFeature('ESHOP0007', 'Двигатели'),
        ],
    },
];

const serviceFeatures = [
    {
        features: [
            { id: 'SRV0001', name: 'Преобразователи частоты' },
            { id: 'SRV0007', name: 'Электродвигатели' },
        ],
    },
];

const federalNetworkCompanies: Search[] = [
    {
        name: 'Федеральные сетевые компании',
        type: 'search',
        features: [
            { id: 'etm', name: 'ЭТМ', value: 'этм' },
            { id: 'rus-svet', name: 'Русский свет', value: 'русский свет' },
            { id: 'minimax', name: 'Минимакс', value: 'минимакс' },
        ],
    },
];

const featuresByFilter: FeaturesByFilter = {
    dist: distributorFeatures,
    engineering: engineeringFeatures,
    eshop: eshopFeatures,
    service: serviceFeatures,
    fsc: federalNetworkCompanies,
};

const RadioFeatures = ({
    features,
    selected,
    setFeatureFilters,
}: {
    features: Feature[];
    selected: StoreState['featureFilters'];
    setFeatureFilters: StoreState['setFeatureFilters'];
}) => {
    const selectedById: Record<string, string | null> = selected.reduce(
        (total, { code, value }) => ({
            ...total,
            [code]: value,
        }),
        {}
    );

    const toggleFeature = (id: string, value: string) => {
        const selected = { ...selectedById };
        selected[id] = value;
        setFeatureFilters(Object.entries(selected).map(([code, value]) => ({ code, value })));
    };

    return (
        <div className="categories">
            {features.map(({ name, features }, idx) => (
                <div key={idx} className="accordion-body">
                    <div className="section-group">
                        <i className="ico-down-open" />
                        {name}
                    </div>
                    <div className="section-prop">
                        {features.map(({ name, id, values }) => (
                            <div key={id} className="radio-group">
                                {name}
                                <div>
                                    {values?.map(({ name, value }) => (
                                        <React.Fragment key={value}>
                                            <input
                                                className="distribution-radio"
                                                name={id}
                                                type="radio"
                                                value={value}
                                                checked={selectedById[id] === value}
                                                onChange={() => toggleFeature(id, value)}
                                            />
                                            &nbsp;{name} <br />
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
const CheckboxFeatures = ({
    features,
    selected,
    setFeatureFilters,
}: {
    features: Feature[];
    selected: StoreState['featureFilters'];
    setFeatureFilters: StoreState['setFeatureFilters'];
}) => {
    const selectedById: Record<string, string | null> = selected.reduce(
        (total, { code, value }) => ({
            ...total,
            [code]: value,
        }),
        {}
    );

    const toggleFeature = (id: string) => {
        const isCurrentlySelected = selectedById[id] === '1';

        if (isCurrentlySelected) {
            const newFilters = selected.filter((filter) => filter.code !== id);
            setFeatureFilters(newFilters);
        } else {
            const filteredOut = selected.filter((filter) => filter.code !== id);
            const newFilters = [...filteredOut, { code: id, value: '1' }];
            setFeatureFilters(newFilters);
        }
    };

    return (
        <div className="categories">
            {features.map(({ name, features }, idx) => (
                <div key={idx} className="accordion-body">
                    <div className="section-group">
                        <i className="ico-down-open" />
                        {name}
                    </div>
                    {features.map(({ name, id }) => (
                        <React.Fragment key={id}>
                            <input
                                className="engineering-check"
                                name={id}
                                type="checkbox"
                                value="1"
                                checked={selectedById[id] === '1'}
                                onChange={() => toggleFeature(id)}
                            />
                            &nbsp;{name} <br />
                        </React.Fragment>
                    ))}
                </div>
            ))}
        </div>
    );
};

const BlockFeatures = ({
    features,
    selected,
    setFeatureFilters,
}: {
    features: FeatureItem[];
    selected: StoreState['featureFilters'];
    setFeatureFilters: StoreState['setFeatureFilters'];
}) => {
    const selectedById: Record<string, string | null> = selected.reduce(
        (total, { code, value }) => ({
            ...total,
            [code]: value,
        }),
        {}
    );
    const toggleFeature = (id: string) => {
        const selected = { ...selectedById };
        for (const key in selected) {
            selected[key] = '_any_';
        }
        selected[id] = '1';
        setFeatureFilters(Object.entries(selected).map(([code, value]) => ({ code, value })));
    };
    return (
        <div className="pl-2 categories" style={{ paddingLeft: '10px', marginTop: '10px' }}>
            {features.map(({ id, name }) => (
                <div
                    key={id}
                    className={'filter-block filter' + (selectedById[id] === '1' ? ' active-filter' : '')}
                    onClick={() => toggleFeature(id)}
                >
                    <label>{name}</label>
                </div>
            ))}
        </div>
    );
};

const BlockSearch = ({
    features,
    search,
    setSearch,
}: {
    features: SearchItem[];
    search: StoreState['search'];
    setSearch: StoreState['setSearch'];
}) => {
    const selected: string = search;
    const toggleFeature = (value: string) => {
        setSearch(value);
    };
    return (
        <div className="pl-2 categories" style={{ paddingLeft: '10px', marginTop: '10px' }}>
            {features.map(({ id, name, value }) => (
                <div
                    key={id}
                    className={'filter-block filter' + (selected === value ? ' active-filter' : '')}
                    onClick={() => toggleFeature(value)}
                >
                    <label>{name}</label>
                </div>
            ))}
        </div>
    );
};
export default function FeatureFilters() {
    const {
        featureFilters,
        setFeatureFilters,
        search,
        setSearch,
        setMultipleSearch,
        activeFilter,
        setActiveFilter,
    } = useContext(StoresContext);

    const toggleFilter = (name: string) => {
        if (activeFilter === name) {
            setActiveFilter(null);
            setFeatureFilters([]);
            setSearch('');
            setMultipleSearch([]);
        } else if (name === FeatureName.FSC) {
            setActiveFilter(name);
            setFeatureFilters([]);
            setSearch('');
            setMultipleSearch([]);

            const source = featuresByFilter[FeatureName.FSC];
            if (source && Array.isArray(source)) {
                const allMultipleSearch: string[] = [];
                for (const item of source) {
                    if (item.features) {
                        for (const feature of item.features) {
                            if (feature.value) {
                                allMultipleSearch.push(feature.value);
                            }
                        }
                    }
                }
                setMultipleSearch(allMultipleSearch);
            }
        } else if (name === FeatureName.ENGINEERING) {
            setActiveFilter('engineering');
            setFeatureFilters([]);
            setSearch('');
            setMultipleSearch([]);
        } else {
            setActiveFilter(name);
            setSearch('');
            setMultipleSearch([]);

            const source = featuresByFilter[name as keyof FeaturesByFilter];

            if (source && Array.isArray(source)) {
                const allFeatures: StoreState['featureFilters'] = [];
                for (const item of source) {
                    if (item.features) {
                        for (const feature of item.features) {
                            if (feature.id) {
                                allFeatures.push({
                                    code: feature.id,
                                    value: '_any_',
                                });
                            }
                        }
                    }
                }
                setFeatureFilters(allFeatures);
            } else {
                setFeatureFilters([]);
            }
        }
    };

    return (
        <div className="col-12">
            <div
                className={
                    'filter-block filter filter-distribution' +
                    (activeFilter === FeatureName.FSC ? ' active-filter' : '')
                }
                onClick={() => toggleFilter(FeatureName.FSC)}
            >
                <span className="round-icon m-r-1">
                    <i className="ico-basket" />
                </span>
                <label>Федеральные сетевые компании</label>
            </div>
            {activeFilter === FeatureName.FSC && (
                <BlockSearch
                    features={federalNetworkCompanies[0].features}
                    search={search}
                    setSearch={setSearch}
                />
            )}
            <div
                className={
                    'filter-block filter filter-distribution' +
                    (activeFilter === FeatureName.DIST ? ' active-filter' : '')
                }
                onClick={() => toggleFilter(FeatureName.DIST)}
            >
                <span className="round-icon m-r-1">
                    <i className="ico-spread" />
                </span>
                <label>Дистрибьюторы</label>
            </div>
            {activeFilter === FeatureName.DIST && (
                <RadioFeatures
                    features={distributorFeatures}
                    selected={featureFilters}
                    setFeatureFilters={setFeatureFilters}
                />
            )}

            <div
                className={
                    'filter-block filter filter-distribution' +
                    (activeFilter === FeatureName.ENGINEERING ? ' active-filter' : '')
                }
                onClick={() => toggleFilter(FeatureName.ENGINEERING)}
            >
                <span className="round-icon m-r-1">
                    <i className="ico-cogs" />
                </span>
                <label>Авторизованные инжиниринговые партнеры</label>
            </div>

            {activeFilter === FeatureName.ENGINEERING && (
                <CheckboxFeatures
                    features={engineeringFeatures}
                    selected={featureFilters}
                    setFeatureFilters={setFeatureFilters}
                />
            )}
            <div
                className={
                    'filter-block filter filter-distribution' +
                    (activeFilter === FeatureName.ESHOP ? ' active-filter' : '')
                }
                onClick={() => toggleFilter(FeatureName.ESHOP)}
            >
                <span className="round-icon m-r-1">
                    <i className="ico-basket" />
                </span>
                <label>Интернет-магазин</label>
            </div>
            {activeFilter === FeatureName.ESHOP && (
                <RadioFeatures
                    features={eshopFeatures}
                    selected={featureFilters}
                    setFeatureFilters={setFeatureFilters}
                />
            )}
            <div
                className={
                    'filter-block filter filter-distribution' +
                    (activeFilter === FeatureName.SERVICE ? ' active-filter' : '')
                }
                onClick={() => toggleFilter(FeatureName.SERVICE)}
            >
                <span className="round-icon m-r-1">
                    <i className="ico-basket" />
                </span>
                <label>Сервисное обслуживание</label>
            </div>
            {activeFilter === FeatureName.SERVICE && (
                <BlockFeatures
                    features={serviceFeatures[0].features}
                    selected={featureFilters}
                    setFeatureFilters={setFeatureFilters}
                />
            )}
        </div>
    );
}
